import { storage } from '@/lib/utils/storage';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

// -----------------------------------------------------
// ENV
// -----------------------------------------------------
const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// -----------------------------------------------------
// Types
// -----------------------------------------------------
type RefreshResponse = {
  accessToken: string;
  refreshToken: string;
};

// Extend AxiosRequestConfig to support _retry flag
interface RetryAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

// -----------------------------------------------------
// Refresh token promise holder
// -----------------------------------------------------
let refreshPromise: Promise<string> | null = null;
let isRefreshing = false;

// -----------------------------------------------------
// Axios Instance
// -----------------------------------------------------
const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// -----------------------------------------------------
// Request Interceptor – Attach Access Token
// -----------------------------------------------------
api.interceptors.request.use((config) => {
  const token = storage.get('token');

  if (token) {
    if (config.headers) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
  }

  return config;
});

// -----------------------------------------------------
// Response Interceptor – Auto Refresh on 401
// -----------------------------------------------------

// api.interceptors.response.use(
//   (response: AxiosResponse) => response,

//   async (error: AxiosError) => {
//     console.log('🚀 ~ error:', error);
//     const originalRequest = error.config as RetryAxiosRequestConfig;

//     // Access token expired
//     if (error.response?.status === 401 && !originalRequest._retry) {
//       originalRequest._retry = true;

//       const refreshToken = storage.get('refresh_token');
//       if (!refreshToken) {
//         handleLogout();
//         return Promise.reject(error);
//       }

//       if (!refreshPromise) {
//         refreshPromise = api
//           .post<RefreshResponse>('/auth/refresh', { refreshToken })
//           .then((res) => {
//             const { accessToken, refreshToken } = res.data;
//             storage.set('token', accessToken);
//             storage.set('refresh_token', refreshToken);
//             return accessToken;
//           })
//           .finally(() => {
//             refreshPromise = null;
//           });
//       }

//       const newToken = await refreshPromise;

//       originalRequest.headers = {
//         ...originalRequest.headers,
//         Authorization: `Bearer ${newToken}`,
//       };

//       return api(originalRequest);
//     }

//     // Refresh token expired or invalid
//     if (error.response?.status === 403 && !refreshPromise) {
//       handleLogout();
//     }

//     return Promise.reject(error);
//   }
// );

// api.interceptors.response.use(
//   (response: AxiosResponse) => response,

//   async (error: AxiosError) => {
//     const originalRequest = error.config as RetryAxiosRequestConfig;

//     // Access token expired
//     if (error.response?.status === 401 && !originalRequest._retry) {
//       originalRequest._retry = true;

//       const refreshToken = storage.get('refresh_token');
//       if (!refreshToken) {
//         handleLogout();
//         return Promise.reject(error);
//       }

//       if (!refreshPromise) {
//         refreshPromise = api
//           .post<RefreshResponse>('/auth/refresh', { refreshToken })
//           .then((res) => {
//             const { accessToken, refreshToken } = res.data;
//             storage.set('token', accessToken);
//             storage.set('refresh_token', refreshToken);
//             return accessToken;
//           })
//           .catch((refreshError: AxiosError) => {
//             // Handle refresh failure HERE
//             handleLogout();
//             throw refreshError; // Re-throw to reject the promise
//           })
//           .finally(() => {
//             refreshPromise = null;
//           });
//       }

//       try {
//         const newToken = await refreshPromise;

//         originalRequest.headers = {
//           ...originalRequest.headers,
//           Authorization: `Bearer ${newToken}`,
//         };

//         return api(originalRequest);
//       } catch (refreshError) {
//         // Refresh failed, error already handled in catch above
//         return Promise.reject(refreshError);
//       }
//     }

//     return Promise.reject(error);
//   }
// );

api.interceptors.response.use(
  (response: AxiosResponse) => response,

  async (error: AxiosError) => {
    const originalRequest = error.config as RetryAxiosRequestConfig;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    // ✅ Check if this error is from the refresh endpoint
    const isRefreshRequest = originalRequest.url === '/auth/refresh';

    // ✅ If refresh request itself failed (401 or 403) - logout immediately
    if (isRefreshRequest && (error.response?.status === 401 || error.response?.status === 403)) {
      console.log('🔴 Refresh token invalid/expired - logging out');
      isRefreshing = false;
      refreshPromise = null;
      handleLogout();
      return Promise.reject(error);
    }

    // Access token expired - try to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = storage.get('refresh_token');
      if (!refreshToken) {
        console.log('🔴 No refresh token found - logging out');
        handleLogout();
        return Promise.reject(error);
      }

      // ✅ If already refreshing, wait for the existing promise
      if (isRefreshing && refreshPromise) {
        try {
          const newToken = await refreshPromise;
          originalRequest.headers = {
            ...originalRequest.headers,
            Authorization: `Bearer ${newToken}`,
          };
          return api(originalRequest);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }

      // ✅ Start new refresh
      isRefreshing = true;

      refreshPromise = api
        .post<RefreshResponse>('/auth/refresh', { refreshToken })
        .then((res) => {
          console.log('🟢 Token refreshed successfully');
          const { accessToken, refreshToken: newRefreshToken } = res.data;
          storage.set('token', accessToken);
          storage.set('refresh_token', newRefreshToken);
          return accessToken;
        })
        .catch((refreshError) => {
          // ✅ This catch handles the error before it propagates
          console.log('🔴 Refresh failed in catch block');
          // Logout is handled by the interceptor when it catches the 403
          throw refreshError;
        })
        .finally(() => {
          isRefreshing = false;
          refreshPromise = null;
        });

      try {
        const newToken = await refreshPromise;
        originalRequest.headers = {
          ...originalRequest.headers,
          Authorization: `Bearer ${newToken}`,
        };
        return api(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// -----------------------------------------------------
// Logout Helper
// -----------------------------------------------------
function handleLogout(): void {
  storage.clear();
  window.location.href = '/login';
}

// -----------------------------------------------------
// Reusable CRUD Factory
// -----------------------------------------------------
type CrudParams = Record<string, unknown>;
type CrudData = Record<string, unknown>;

const crud = (resource: string) => ({
  getAll: (params: CrudParams = {}) => api.get(`/${resource}`, { params }),

  getById: (id: string) => api.get(`/${resource}/${id}`),

  create: (data: CrudData) => api.post(`/${resource}`, data),

  update: (id: string, data: CrudData) => api.put(`/${resource}/${id}`, data),

  delete: (id: string) => api.delete(`/${resource}/${id}`),

  // Form-data versions
  createForm: (data: FormData) =>
    api.post(`/${resource}`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  updateForm: (id: string, data: FormData) =>
    api.put(`/${resource}/${id}`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
});

// -----------------------------------------------------
// API Groups
// -----------------------------------------------------
export const authAPI = {
  login: (email: string, password: string) => api.post('/auth/login', { email, password }),

  me: () => api.get('/auth/profile'),
};

export const companiesAPI = {
  ...crud('companies'),
  uploadLogo: (id: string, data: FormData) =>
    api.post(`/companies/${id}/logo`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

export const customersAPI = crud('customers');
export const itemsAPI = crud('items');
export const bankDetailsAPI = crud('bank-details');
export const invoicesAPI = crud('invoices');

export default api;
